import { useTheme } from "@mui/material";
import { Button } from "@totalenergiescode/mobility-business-rev-design-system";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetMigrationReports } from "@/api/graphql/migration/hooks";
import { RoundedTopLeftContainer } from "@/components/containers/RoundedTopLeftContainer/component";

import { ErrorComponent } from "../shared/Error/component";
import { Loading } from "../shared/Loading/component";
import { Downloads } from "./Downloads/component";
import {
  useDownloadMigrationResults,
  useGetMigrationReportsColumns,
  useGetMigrationReportsTable,
  useHandleLoadAccounts,
} from "./hooks";
import { MigrationDrawer } from "./MigrationDrawer/component";
import {
  StyledCountText,
  StyledMigrationReportContainer,
  StyledMigrationReportsCount,
  StyledMigrationReportTextAreaContainer,
  StyledPageHeader,
  StyledSearchButton,
  StyledSortableCustomTable,
  StyledTextArea,
  StyledTextAreaContainer,
} from "./styles";
import { MigrationReportSuccessComponent } from "./Success/component";

export const MigrationReport = () => {
  const theme = useTheme();
  const { t: tCommon } = useTranslation("common");
  const { t: tMigrationReport } = useTranslation("migrationReport");
  const [accountsInput, setAccountsInput] = useState<string>("");
  const [accounts, setAccounts] = useState<string[] | undefined>();

  const [isMigrationModalOpen, setIsMigrationModalOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useGetMigrationReports({
    accountNumbers: accounts,
  });
  const { columns } = useGetMigrationReportsColumns();
  const { rows: successfulRows } = useGetMigrationReportsTable({
    migrations: data?.successfulMigrations,
  });
  const { rows: failedRows } = useGetMigrationReportsTable({
    migrations: data?.failedMigrations,
  });

  const { handleLoadAccounts } = useHandleLoadAccounts(setAccounts);
  const { downloadMigrationResults } = useDownloadMigrationResults();
  const breadcrumbItems = [
    {
      title: tCommon("common.features.dashboard"),
      path: "/" as const,
    },
    {
      title: tCommon("common.sidebar.migrationReport"),
      path: "/backoffice-users" as const,
    },
  ];
  const diagnosedAccounts = data?.diagnosedAccounts || 0;
  const migratedAccounts = data?.migratedAccounts || 0;

  // FIX: useless memoization
  const isSuccess = useMemo(
    () =>
      diagnosedAccounts === migratedAccounts &&
      diagnosedAccounts !== 0 &&
      migratedAccounts !== 0 &&
      !!data?.successfulMigrations.length,
    [diagnosedAccounts, migratedAccounts, data?.successfulMigrations.length],
  );

  function toggleMigrationModal() {
    setIsMigrationModalOpen((state) => !state);
  }

  return (
    <>
      <StyledPageHeader
        breadcrumbItems={breadcrumbItems}
        title={tCommon("common.sidebar.migrationReport")}
        href=''
      />
      <RoundedTopLeftContainer>
        <StyledMigrationReportContainer>
          <StyledMigrationReportTextAreaContainer>
            <StyledTextAreaContainer>
              <StyledTextArea
                multiline
                maxRows={2}
                placeholder={tMigrationReport(
                  "migrationReport.inputPlaceholder",
                )}
                value={accountsInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setAccountsInput(e.target.value);
                }}
              />
            </StyledTextAreaContainer>

            <StyledSearchButton>
              <Button
                variant='secondary'
                isLowercase
                buttonText={tMigrationReport(
                  "migrationReport.actions.syncButton",
                )}
                onClick={toggleMigrationModal}
              />
              <Button
                variant='primary'
                isLowercase
                buttonText={tMigrationReport(
                  "migrationReport.actions.searchButton",
                )}
                onClick={() => handleLoadAccounts(accountsInput)}
              />
              <Button
                variant='secondary'
                isLowercase
                buttonText={tMigrationReport(
                  "migrationReport.actions.resetButton",
                )}
                onClick={() => {
                  setAccounts(undefined);
                  setAccounts(undefined);
                  setAccountsInput("");
                }}
              />
              <Button
                variant='secondary'
                isLowercase
                buttonText={tMigrationReport("migrationReport.actions.refresh")}
                onClick={() => refetch()}
              />
              <Downloads
                downloadMigrationResults={downloadMigrationResults}
                translateFunction={tMigrationReport}
                data={data}
              />
            </StyledSearchButton>
          </StyledMigrationReportTextAreaContainer>

          <StyledMigrationReportsCount>
            {diagnosedAccounts > 0 && (
              <StyledCountText
                variant='title1'
                sx={{ color: theme.palette.grey[700] }}
              >
                {tMigrationReport("migrationReport.diagnosedAccounts.count", {
                  count: diagnosedAccounts,
                })}
              </StyledCountText>
            )}
            {!!data?.successfulMigrations.length &&
              data?.successfulMigrations.length > 0 && (
                <StyledCountText
                  variant='title1'
                  sx={{ color: theme.palette.secondary.green[400] }}
                >
                  {tMigrationReport("migrationReport.migratedAccounts.count", {
                    count: data?.successfulMigrations.length,
                  })}
                </StyledCountText>
              )}
            {!!data?.failedMigrations.length &&
              data?.failedMigrations.length > 0 && (
                <StyledCountText
                  variant='title1'
                  sx={{ color: theme.palette.secondary.red[300] }}
                >
                  {tMigrationReport("migrationReport.unmigratedAccount.count", {
                    count: data?.failedMigrations.length,
                  })}
                </StyledCountText>
              )}
          </StyledMigrationReportsCount>
          <Loading
            isLoading={isLoading}
            secondaryText={tMigrationReport(
              "migrationReport.loading.secondaryText",
            )}
            principalText={tMigrationReport(
              "migrationReport.loading.principalText",
            )}
          >
            <ErrorComponent
              isError={isError}
              principalText={tMigrationReport(
                "migrationReport.errorMessage.principalText",
              )}
              secondaryText={tMigrationReport(
                "migrationReport.errorMessage.secondaryText",
              )}
            >
              <MigrationReportSuccessComponent isSuccess={isSuccess}>
                <>
                  {Boolean(failedRows.length) && (
                    <StyledSortableCustomTable
                      canSelectRows={false}
                      rows={failedRows}
                      columns={columns}
                    />
                  )}
                  {Boolean(successfulRows.length) && (
                    <StyledSortableCustomTable
                      canSelectRows={false}
                      rows={successfulRows}
                      columns={columns}
                    />
                  )}
                </>
              </MigrationReportSuccessComponent>
            </ErrorComponent>
          </Loading>
        </StyledMigrationReportContainer>
      </RoundedTopLeftContainer>
      <MigrationDrawer
        setIsMigrationModalOpen={toggleMigrationModal}
        isMigrationModalOpen={isMigrationModalOpen}
      />
    </>
  );
};
